// components/clubs/clubDataExportUtils.ts
// -----------------------------------------------------------------------------
// Small pure helpers for the club data export UI. Kept out of the component so
// they can be unit tested without rendering.
// -----------------------------------------------------------------------------

import { DateTime } from "luxon";
import type { ClubExportJob } from "@/api/clubsApi";

/** A job older than this while still pending/processing is treated as stale. */
export const staleJobMinutes = 30;

/**
 * Bytes per second assumed before the job has streamed enough media to measure.
 * ~1 MB/s, taken from the first live export — good enough for a first guess that
 * the observed rate replaces within half a minute.
 */
export const fallbackMediaBytesPerSecond = 1_000_000;

/**
 * Media must have been streaming at least this long before its own rate is
 * trusted. The first few seconds are dominated by connection setup, so an early
 * sample would predict wildly.
 */
export const mediaRateSampleSeconds = 20;

/** True while the job is queued or being built. */
export function isJobInFlight(job: Pick<ClubExportJob, "status">): boolean {
  return job.status === "pending" || job.status === "processing";
}

/**
 * True when an in-flight job was created more than `staleJobMinutes` ago.
 * Stale jobs stop blocking a new request — the edge function allows a retry too.
 */
export function isJobStale(
  job: Pick<ClubExportJob, "status" | "created_at">,
  now: Date = new Date()
): boolean {
  if (!isJobInFlight(job)) return false;

  const created = DateTime.fromISO(job.created_at);
  if (!created.isValid) return false;

  return created.diff(DateTime.fromJSDate(now), "minutes").minutes < -staleJobMinutes;
}

/** True when a download link's expiry has passed. Missing expiry never expires. */
export function isExpired(
  expiresAt: string | null | undefined,
  now: Date = new Date()
): boolean {
  if (!expiresAt) return false;

  const expires = DateTime.fromISO(expiresAt);
  if (!expires.isValid) return false;

  return expires.toMillis() <= now.getTime();
}

/** Human label for a job row. */
export function formatJobStatus(job: ClubExportJob, now: Date = new Date()): string {
  switch (job.status) {
    case "ready":
      return isExpired(job.expires_at, now) ? "Expired" : "Ready";
    case "failed":
      return "Failed";
    case "processing":
    case "pending":
    default:
      return "Preparing…";
  }
}

/** Rounded, human-sized bytes, e.g. "48 KB", "352 MB", "1.2 GB". */
export function formatBytes(bytes: number | null | undefined): string {
  if (typeof bytes !== "number" || !Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1000) return `${Math.round(bytes)} B`;

  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1000;
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }

  // One decimal below 10 keeps "1.2 GB" useful; bigger numbers read better whole.
  const rounded = value < 10 ? Math.round(value * 10) / 10 : Math.round(value);
  return `${rounded} ${units[unit]}`;
}

/** What an in-flight job's row should say about its progress. */
export interface JobProgressDescription {
  /** The status label, replacing the generic "Preparing…". */
  label: string;
  /** How much media has been packaged, when that is known. */
  detail: string | null;
  /** Rough time remaining, when it can be estimated. */
  eta: string | null;
}

/** Reads a finite number out of the loosely typed progress object. */
function readNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** Reads a non-empty string out of the loosely typed progress object. */
function readString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

/** Rounds seconds remaining into a bucket vague enough to stay true. */
function formatTimeLeft(seconds: number): string {
  if (seconds < 45) return "less than a minute left";
  if (seconds < 90) return "about a minute left";
  if (seconds < 55 * 60) {
    // Below 90s already reads as "a minute", so the plural starts at two.
    return `about ${Math.max(Math.round(seconds / 60), 2)} minutes left`;
  }
  if (seconds < 90 * 60) return "about an hour left";
  return `about ${Math.round(seconds / 3600)} hours left`;
}

/**
 * Describes what an in-flight export is doing, from the `progress` object the
 * export writes at every checkpoint. Every field is treated as possibly missing
 * or wrongly typed: an unrecognisable progress object falls back to the plain
 * "Preparing…" the row showed before this existed.
 *
 * Pure and parameterised on `now` so the ETA is testable.
 */
export function describeJobProgress(
  job: ClubExportJob,
  now: Date = new Date()
): JobProgressDescription {
  const preparing: JobProgressDescription = {
    label: "Preparing…",
    detail: null,
    eta: null,
  };

  const progress = job.progress;
  if (!progress || typeof progress !== "object" || Array.isArray(progress)) {
    return preparing;
  }

  const step = readString(progress.step);
  // The text phase is quick and has nothing worth counting; anything past media
  // ("finishing", "done") is the tail of the run, with nothing left to measure.
  if (step === null || step === "text") return preparing;
  if (step !== "media") return { label: "Finishing up", detail: null, eta: null };

  const mediaBytes = Math.max(readNumber(progress.media_bytes) ?? 0, 0);
  const totalBytes = readNumber(progress.media_total_bytes);
  const hasTotal = totalBytes !== null && totalBytes > 0;

  const detail = hasTotal
    ? `${formatBytes(mediaBytes)} of ${formatBytes(totalBytes)}`
    : `${formatBytes(mediaBytes)} so far`;

  if (!hasTotal) return { label: "Packaging media", detail, eta: null };

  const startedAt = readString(progress.media_started_at);
  const started = startedAt ? DateTime.fromISO(startedAt) : null;
  const elapsedSeconds =
    started && started.isValid ? (now.getTime() - started.toMillis()) / 1000 : 0;

  // Trust the job's own throughput only once there is enough of it to average.
  const bytesPerSecond =
    elapsedSeconds >= mediaRateSampleSeconds && mediaBytes > 0
      ? mediaBytes / elapsedSeconds
      : fallbackMediaBytesPerSecond;

  const remainingBytes = Math.max(totalBytes - mediaBytes, 0);
  const secondsLeft = remainingBytes / bytesPerSecond;

  return { label: "Packaging media", detail, eta: formatTimeLeft(secondsLeft) };
}

/** Short absolute date+time, e.g. "Sep 5, 2026, 2:04 PM". */
export function formatExportDate(iso: string | null | undefined): string {
  if (!iso) return "";

  const dt = DateTime.fromISO(iso);
  if (!dt.isValid) return "";

  return dt.toLocaleString(DateTime.DATETIME_MED);
}

/** True when at least one job in the list is still being worked on. */
export function hasJobInFlight(jobs: ClubExportJob[] | undefined): boolean {
  return (jobs ?? []).some(isJobInFlight);
}
